import { useState } from "react";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import type { PendingUpload, UploadReceipt } from "@/payroll/editor/types";

const readReceipt = (key: string): UploadReceipt | null => {
  try {
    const item = JSON.parse(sessionStorage.getItem(key) || "null");
    return typeof item?.fingerprint === "string" &&
      typeof item?.clientId === "string"
      ? item
      : null;
  } catch {
    return null;
  }
};

export const useEditorUpload = (
  storageKey: string,
  mutation: PayrollEditorQueries["upload"],
) => {
  const [receipt, setReceipt] = useState(() => readReceipt(storageKey));
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const [localError, setLocalError] = useState("");
  const clear = () => {
    sessionStorage.removeItem(storageKey);
    setReceipt(null);
    setPending(null);
  };
  const upload = async (item: PendingUpload) => {
    setPending(item);
    try {
      await mutation.mutateAsync(item);
      clear();
    } catch {
      // Keep the file and receipt so Retry uses the same upload ID.
    }
  };
  const selectFile = (file: File) => {
    setLocalError("");
    if (file.size > EDITOR_LIMITS.fileBytes) {
      setLocalError("Choose a file that is 20 MB or smaller.");
      return;
    }
    const fingerprint = `${file.name}:${file.size}:${file.lastModified}`;
    if (receipt && receipt.fingerprint !== fingerprint) {
      setLocalError("Select the same file to finish the interrupted upload.");
      return;
    }
    const clientId = receipt?.clientId ?? crypto.randomUUID();
    const nextReceipt = { fingerprint, clientId };
    sessionStorage.setItem(storageKey, JSON.stringify(nextReceipt));
    setReceipt(nextReceipt);
    void upload({ file, clientId });
  };
  const chooseAnotherFile = () => {
    clear();
    mutation.reset();
  };
  return {
    pending,
    hasReceipt: !!receipt,
    localError,
    selectFile,
    upload,
    chooseAnotherFile,
    isUploading: mutation.isPending,
    error: mutation.error,
  };
};

export type EditorUpload = ReturnType<typeof useEditorUpload>;
